import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

// ─────────────────────────────────────────────────────────────────────────────
// Docking dashboard read API (`/v1/dashboard/*`) — leadership hub-performance
// metrics computed from daily snapshots. Types mirror the BE response
// entities in nest-logistic-service `src/modules/dashboard/domain/entities`.
//
// Semantics the UI must respect (dock-context-v1.md):
//   • value `null` always means "no data", never 0.
//   • status INSUFFICIENT_SAMPLES marks thin windows; UNAVAILABLE marks
//     metrics blocked on the delivery-service integration.
//   • All dates are WIB (Asia/Jakarta); `freshness` carries the window.
// ─────────────────────────────────────────────────────────────────────────────

export type DashboardWindow = '1d' | '7d' | '30d' | '60d';

export type MetricStatus = 'OK' | 'INSUFFICIENT_SAMPLES' | 'UNAVAILABLE';

export type TrendDirection = 'up' | 'down' | 'flat';

export interface DashboardFreshness {
  /** Most recent snapshot date observed (WIB); null when the window is empty. */
  dataAsOf: string | null;
  /** True when the most recent expected snapshot is missing. */
  stale: boolean;
  windowDays: number;
  windowStart: string;
  windowEnd: string;
  timezone: string;
}

export interface DashboardTrend {
  direction: TrendDirection;
  percentChange: number | null;
  priorMa: number | null;
}

export type VolumeStressBand = 'NORMAL' | 'ELEVATED' | 'AT_CEILING';

export interface VolumeStress {
  ratio: number | null;
  band: VolumeStressBand | null;
  windowAvgItemsPerDay: number | null;
  trailingP90ItemsPerDay: number | null;
  shortHistory: boolean;
  /** OK | INSUFFICIENT_HISTORY */
  status: string;
}

// ── Overview (GET /v1/dashboard/hubs) ────────────────────────────────────────

export interface HubOverviewVelocityCell {
  value: number | null;
  trend: DashboardTrend;
  status: MetricStatus;
}

export interface HubOverviewRow {
  hubId: number;
  hubName: string;
  inboundVelocity: HubOverviewVelocityCell;
  dispatchVelocity: HubOverviewVelocityCell;
  riderScanVelocity: HubOverviewVelocityCell;
  plannerStress: number;
  plannerStressMa: number | null;
  scannedCount: number;
  coverage: number;
  lowCoverage: boolean;
  volumeStress: VolumeStress;
}

export interface HubOverviewResponse {
  freshness: DashboardFreshness;
  rows: HubOverviewRow[];
  coverageThreshold: number;
}

// ── Stage velocities ─────────────────────────────────────────────────────────

export interface InboundVelocityDay {
  date: string;
  waveCount: number;
  itemCount: number;
  totalDurationSec: number;
  secPerItem: number | null;
  maSecPerItem: number | null;
}

export interface InboundWave {
  startAt: string;
  endAt: string;
  itemCount: number;
  durationSec: number;
}

export interface InboundVelocityResponse {
  hubId: number;
  hubName: string;
  freshness: DashboardFreshness;
  status: MetricStatus;
  currentMaSecPerItem: number | null;
  trend: DashboardTrend;
  days: InboundVelocityDay[];
  latestWave: InboundWave | null;
}

export interface DispatchVelocityDay {
  date: string;
  dispatchCount: number;
  itemCount: number;
  totalDurationSec: number;
  secPerItem: number | null;
  maSecPerItem: number | null;
}

/**
 * Dispatch duration per dispatch = committed_at − max(created_at, last
 * inbound scan of the dispatch's items) — under the preview-before-inbound
 * flow the preview window overlaps inbound, so raw preview→commit would
 * bill the whole inbound stage to dispatch.
 */
export interface DispatchVelocityResponse {
  hubId: number;
  hubName: string;
  freshness: DashboardFreshness;
  status: MetricStatus;
  currentMaSecPerItem: number | null;
  trend: DashboardTrend;
  days: DispatchVelocityDay[];
}

export interface RiderScanVelocityDay {
  date: string;
  batchCount: number;
  itemCount: number;
  totalDurationSec: number;
  secPerItem: number | null;
  maSecPerItem: number | null;
}

export interface RiderScanDistribution {
  sampleSize: number;
  minSec: number | null;
  medianSec: number | null;
  maxSec: number | null;
}

/** Rider punctuality: dispatch commit → the batch's first handover scan.
 *  Separate from velocity so late riders don't read as slow scanning. */
export interface RiderArrivalLag {
  sampleSize: number;
  medianSec: number | null;
  maxSec: number | null;
  lateBatchCount: number;
  lateThresholdSec: number;
}

export interface RiderScanVelocityResponse {
  hubId: number;
  hubName: string;
  freshness: DashboardFreshness;
  status: MetricStatus;
  currentMaSecPerItem: number | null;
  trend: DashboardTrend;
  distribution: RiderScanDistribution;
  arrivalLag: RiderArrivalLag;
  days: RiderScanVelocityDay[];
}

// ── Planner stress / throughput / zones ──────────────────────────────────────

export interface PlannerStressDay {
  date: string;
  unbatchable: number;
  deferred: number;
  total: number;
  maTotal: number | null;
}

export interface PlannerStressResponse {
  hubId: number;
  hubName: string;
  freshness: DashboardFreshness;
  currentMaTotal: number | null;
  trend: DashboardTrend;
  days: PlannerStressDay[];
}

export type ThroughputGranularity = 'hourly' | 'daily';

export interface ThroughputBucket {
  /** ISO8601 bucket start (UTC) — render in WIB. */
  bucketStart: string;
  scanned: number;
  dispatched: number;
  handedOver: number;
}

export interface ThroughputResponse {
  hubId: number;
  hubName: string;
  freshness: DashboardFreshness;
  granularity: ThroughputGranularity;
  buckets: ThroughputBucket[];
  totals: { scanned: number; dispatched: number; handedOver: number };
}

export interface ZoneRiderDistributionDay {
  date: string;
  singleRiderCoverages: number;
  multiRiderCoverages: number;
  singleRiderShare: number | null;
  maSingleRiderShare: number | null;
}

export interface ZoneScanTimeRow {
  zoneCode: string;
  zoneName: string;
  itemCount: number;
  minSec: number | null;
  medianSec: number | null;
  maxSec: number | null;
  maSecPerItem: number | null;
  priorMaSecPerItem: number | null;
  trendDirection: TrendDirection;
  unassigned: boolean;
}

export interface ZoneRiderHeadline {
  singleRiderShare: number | null;
  priorSingleRiderShare: number | null;
  totalSingleRider: number;
  totalMultiRider: number;
}

export interface ZoneBreakdownResponse {
  hubId: number;
  hubName: string;
  freshness: DashboardFreshness;
  riderHeadline: ZoneRiderHeadline;
  riderDays: ZoneRiderDistributionDay[];
  zoneScanTime: ZoneScanTimeRow[];
}

// ── Stage timeline (waterfall with idle gaps) ────────────────────────────────

export type StageTimelineKey =
  | 'inbound'
  | 'gap1'
  | 'dispatch'
  | 'gap2'
  | 'riderScan';

export interface StageTimelineStage {
  key: StageTimelineKey;
  medianDurationSec: number | null;
  /** Median items per cycle for work stages; null for idle gaps. */
  medianItemCount: number | null;
}

export interface StageTimelineResponse {
  hubId: number;
  hubName: string;
  freshness: DashboardFreshness;
  status: MetricStatus;
  cycles: {
    count: number;
    /** Cycles where a raw gap was negative before clamping (preview overlapped inbound). */
    overlappedCycles: number;
  };
  stages: StageTimelineStage[];
}

// ── Coverage ─────────────────────────────────────────────────────────────────

export type EtlStatus = 'OK' | 'EMPTY' | 'PARTIAL' | 'FAILED';

export interface CoverageDay {
  date: string;
  present: boolean;
  etlStatus: EtlStatus | null;
}

export interface CoverageResponse {
  hubId: number;
  hubName: string;
  freshness: DashboardFreshness;
  coverage: number;
  threshold: number;
  lowCoverage: boolean;
  expectedDays: number;
  presentDays: number;
  /** Present-but-excluded (PARTIAL/FAILED) days — never silently invisible. */
  excludedDays: number;
  days: CoverageDay[];
}

// ── Hub detail (composite) ───────────────────────────────────────────────────

export interface PendingMetric {
  key: string;
  label: string;
  status: string;
  reason: string;
}

export interface HubDetailResponse {
  hubId: number;
  hubName: string;
  freshness: DashboardFreshness;
  inbound: InboundVelocityResponse;
  dispatch: DispatchVelocityResponse;
  riderScan: RiderScanVelocityResponse;
  plannerStress: PlannerStressResponse;
  throughput: ThroughputResponse;
  zoneBreakdown: ZoneBreakdownResponse;
  coverage: CoverageResponse;
  volumeStress: VolumeStress;
  pendingMetrics: PendingMetric[];
}

// ── Compare ──────────────────────────────────────────────────────────────────

export type CompareMetric =
  | 'inbound_velocity'
  | 'dispatch_velocity'
  | 'rider_scan_velocity'
  | 'planner_stress'
  | 'throughput_scanned';

export interface CompareHubSeriesPoint {
  date: string;
  value: number | null;
  ma: number | null;
}

export interface CompareHubSeries {
  hubId: number;
  hubName: string;
  status: MetricStatus;
  currentMa: number | null;
  lowCoverage: boolean;
  points: CompareHubSeriesPoint[];
}

export interface CompareHubsResponse {
  metric: CompareMetric;
  unit: string;
  freshness: DashboardFreshness;
  series: CompareHubSeries[];
}

// ─────────────────────────────────────────────────────────────────────────────

export interface DashboardWindowParams {
  window?: DashboardWindow;
  /** Anchor the window end to a historical WIB date (YYYY-MM-DD). */
  asOf?: string;
}

type Envelope<T> = { status: string | number; message?: string; data: T };

const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

const get = async <T>(url: string, params?: object): Promise<T> => {
  try {
    const response = await Axios.get(url, { params });
    return unwrap<T>(response.data);
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const dashboardAPI = {
  /** All-hubs overview table. */
  hubs: (params: DashboardWindowParams = {}): Promise<HubOverviewResponse> =>
    get('/v1/dashboard/hubs', params),

  /** Composite hub detail — every panel in one call. */
  hubDetail: (
    hubId: number,
    params: DashboardWindowParams = {}
  ): Promise<HubDetailResponse> => get(`/v1/dashboard/hubs/${hubId}`, params),

  inboundVelocity: (
    hubId: number,
    params: DashboardWindowParams = {}
  ): Promise<InboundVelocityResponse> =>
    get(`/v1/dashboard/hubs/${hubId}/inbound-velocity`, params),

  dispatchVelocity: (
    hubId: number,
    params: DashboardWindowParams = {}
  ): Promise<DispatchVelocityResponse> =>
    get(`/v1/dashboard/hubs/${hubId}/dispatch-velocity`, params),

  riderScanVelocity: (
    hubId: number,
    params: DashboardWindowParams = {}
  ): Promise<RiderScanVelocityResponse> =>
    get(`/v1/dashboard/hubs/${hubId}/rider-scan-velocity`, params),

  plannerStress: (
    hubId: number,
    params: DashboardWindowParams = {}
  ): Promise<PlannerStressResponse> =>
    get(`/v1/dashboard/hubs/${hubId}/planner-stress`, params),

  throughput: (
    hubId: number,
    params: DashboardWindowParams & {
      granularity?: ThroughputGranularity;
    } = {}
  ): Promise<ThroughputResponse> =>
    get(`/v1/dashboard/hubs/${hubId}/throughput`, params),

  zoneBreakdown: (
    hubId: number,
    params: DashboardWindowParams = {}
  ): Promise<ZoneBreakdownResponse> =>
    get(`/v1/dashboard/hubs/${hubId}/zone-breakdown`, params),

  stageTimeline: (
    hubId: number,
    params: DashboardWindowParams = {}
  ): Promise<StageTimelineResponse> =>
    get(`/v1/dashboard/hubs/${hubId}/stage-timeline`, params),

  coverage: (
    hubId: number,
    params: DashboardWindowParams = {}
  ): Promise<CoverageResponse> =>
    get(`/v1/dashboard/hubs/${hubId}/coverage`, params),

  /** Cross-hub series for one metric. 2–10 hubs. */
  compare: (
    hubIds: number[],
    metric: CompareMetric,
    params: DashboardWindowParams = {}
  ): Promise<CompareHubsResponse> =>
    get('/v1/dashboard/compare', {
      ...params,
      hubs: hubIds.join(','),
      metric,
    }),
};
